/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo} from 'react';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {AugmentedSmarterTrailerSensorData, CircuitSummary, SensorTypes} from '@ion/core/src/types/entities';
import {dateFormatter, timeFormatter} from '@ion/core/src/utils/DateUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import LightOutDetectionWidgetIcon from '../../icons/LightOutDetectionWidgetIcon';
import {getTimeDifference, validateLightOutDetectionConnection} from './helper';
import WidgetContainer from './WidgetContainer';
import WidgetDescription from './WidgetDescription';

export type LightOutDetectionWidgetProps = {
  timestamp: string | Date;
  trailerData: AugmentedSmarterTrailerSensorData;
  sensorType?: SensorTypes;
};

/**
 * Defines the Light Out Detection Widget component.
 *
 * ```typescript
 *
 * import {LightOutDetectionWidget} from '@ion/dls/src/components/smarterTrailer';
 *
 * <LightOutDetectionWidget trailerData={trailerData} />
 *
 * ```
 */
export const LightOutDetectionWidget = ({
  timestamp,
  trailerData
}: LightOutDetectionWidgetProps) => {
  if (timestamp) {
    const {LMS: {LA1: lightOutData}} = trailerData || {};
    const today       = getTimeDifference(timestamp);
    const messageDate = dateFormatter(timestamp, UserPreferences.getSystemDateFormat());
    const messageTime = isNil(today) ? timeFormatter(timestamp, UserPreferences.getSystemTimeFormat()) : today;
    const status      = validateLightOutDetectionConnection(lightOutData);
    const label       = translate('common:smarterTrailer.lightOutDetection.widgetLabel');
    const value       = translate(`common:smarterTrailer.lightOutDetection.status.${status?.toLowerCase()}`);
    const style       = {color: status === CircuitSummary.FAULT ? dlsColor('caRed') : dlsColor('grey2')};

    return (
      <WidgetContainer>
        <LightOutDetectionWidgetIcon />
        <WidgetDescription label={label}
          value={value}
          messageTime={messageTime}
          messageDate={messageDate}
          valueStyle={style} />
      </WidgetContainer>
    );
  }
  return null;
};

export default memo(LightOutDetectionWidget);
